import { Box, Checkbox, Chip, FormControlLabel, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  PointerSensor,
  closestCorners,
  pointerWithin,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import React, { useEffect, useState } from "react";
import BackEndColumn from "./backend-column/column";
import FileColumn from "./front-column/column";
import {
  applyColumnDrag,
  assignExactColumnMatches,
  buildInitialColumns,
  createBackendColumnList,
  createFileColumnList,
  getFieldName,
  getSpreadsheetColumnName
} from "../utils";

function importCollisionDetection(args) {
  const pointerHits = pointerWithin(args);
  if (pointerHits.length > 0) {
    return pointerHits;
  }
  return closestCorners(args);
}

const MappingList = ({
  setFileRowsAmount,
  fileColumnNames,
  setIsFirstRowColumnated,
  isFirstRowColumnated,
  setColumnsToSend,
  backendColumnNames,
  fileDataAmount,
  columnsToSend
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dragTextColor = isDark ? "#fff" : "#000";
  const [columns, setColumns] = useState(() => buildInitialColumns(fileColumnNames, backendColumnNames, columnsToSend));
  const [activeField, setActiveField] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    })
  );

  useEffect(() => {
    setColumnsToSend(columns);
  }, [columns, setColumnsToSend]);

  useEffect(() => {
    if (!backendColumnNames?.length) return;
    setColumns((prev) => {
      if (prev.BACKEND.list.length > 0) return prev;
      const matched = assignExactColumnMatches(prev.FILE.list, backendColumnNames);
      const tokensUnchanged = matched.fileList.every((col, index) => col.token === prev.FILE.list[index]?.token);
      if (tokensUnchanged && matched.backendNames.length === prev.BACKEND.list.length) return prev;
      return {
        ...prev,
        FILE: { ...prev.FILE, list: matched.fileList },
        BACKEND: {
          ...prev.BACKEND,
          list: createBackendColumnList(matched.backendNames)
        }
      };
    });
  }, [backendColumnNames]);

  const onDragStart = ({ active }) => {
    setActiveField(getFieldName(active.id));
  };

  const onDragCancel = () => {
    setActiveField(null);
  };

  const onDragEnd = ({ active, over }) => {
    setActiveField(null);
    setColumns((prev) => {
      const next = applyColumnDrag({
        fileList: prev.FILE.list,
        backendList: prev.BACKEND.list,
        activeId: active?.id,
        overId: over?.id
      });
      return {
        ...prev,
        FILE: { ...prev.FILE, list: next.fileList },
        BACKEND: { ...prev.BACKEND, list: next.backendList }
      };
    });
  };

  const updateFileColumnNames = (e) => {
    const checked = e.target.checked;
    const nextNames = checked
      ? fileColumnNames
      : (fileColumnNames || []).map((_, index) => getSpreadsheetColumnName(index + 1));

    setColumns((prev) => {
      const nextFileList = createFileColumnList(nextNames, prev.FILE.list);
      if (!checked) {
        return {
          ...prev,
          FILE: {
            ...prev.FILE,
            list: nextFileList
          }
        };
      }

      const matched = assignExactColumnMatches(
        nextFileList,
        prev.BACKEND.list.map((item) => item.columnName)
      );
      return {
        ...prev,
        FILE: { ...prev.FILE, list: matched.fileList },
        BACKEND: { ...prev.BACKEND, list: createBackendColumnList(matched.backendNames) }
      };
    });

    if (fileDataAmount > 0 && !checked) {
      setFileRowsAmount((prev) => prev + 1);
    }
    if (fileDataAmount > 0 && checked) {
      setFileRowsAmount((prev) => prev - 1);
    }
    setIsFirstRowColumnated(checked);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={importCollisionDetection}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={onDragStart}
      onDragCancel={onDragCancel}
      onDragEnd={onDragEnd}
    >
      <Box>
        <FormControlLabel
          sx={{ mb: 0 }}
          label="First Row Contains Column Names"
          control={<Checkbox checked={isFirstRowColumnated} onChange={updateFileColumnNames} />}
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, mt: -0.5 }}>
          Matching names are filled in automatically. Drag a column to change a mapping.
        </Typography>
        <Box display="flex" sx={{ overflow: "hidden", height: 280, gap: 1.5 }}>
          <FileColumn column={columns.FILE} />
          <BackEndColumn column={columns.BACKEND} />
        </Box>
      </Box>
      <DragOverlay dropAnimation={null} style={{ zIndex: 1700 }} zIndex={1700}>
        {activeField ? (
          <Chip
            size="small"
            label={activeField}
            sx={{
              cursor: "grabbing",
              color: dragTextColor,
              bgcolor: isDark ? alpha("#7dcea0", 0.28) : "#e7f7ee",
              borderColor: isDark ? alpha("#9ed9b6", 0.85) : "#8dcea8",
              "& .MuiChip-label": { color: dragTextColor }
            }}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default MappingList;
